import { Zap, LucideIcon, IterationCcw, Smile } from "lucide-react";

export type ChoiceSelections = { [key: string]: string | boolean };

export class ValidationError extends Error {
  constructor(public error: string) {
    super(error);
  }
}

export type SignupState = {
  goals?: ChoiceSelections;
  household?: ChoiceSelections;
  superpowers?: ChoiceSelections;
  interruptTasks?: boolean;
  interruptAboutAdvisors?: boolean;
  flowPath?: STEPS.CONSULTATION_DETAILS | STEPS.MEMBERSHIP;
  membership?: MembershipOption;
  fun?: ChoiceSelections;
  lifeEvents?: ChoiceSelections;
  familyInfo?: string;
  initialTasks?: string[];
  // todoList?: string;
  anythingElse?: string;
  referralSource?: string;
  payment?: {
    paymentMethodId: string;
    customerId: string;
    promoCode?: string;
  };
  consultationDetails?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    zipCode: string;
  };
  userInfo?: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address: string;
    timezone?: string;
  };
  partnerInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  consultation?: {
    eventUri: string;
    inviteeUri: string;
    eventDate?: Date;
  };
  referralCode?: string;
  termsOfServiceVersion?: number;
  buttonParam?: string;
};

export interface StepRef {}

export interface StepProps {
  onNext?: (
    updates?: Partial<SignupState>,
  ) => Promise<{ token?: string } | void>;
  signupState: SignupState;
  disabled: boolean;
  isDebug: boolean;
}

export interface MembershipOption {
  name: string;
  supportHours: number;
  maxUsers: number;
  amountCents: number;
  additionalFeatures?: string[];
  description?: string;
  descriptionOverride?: string;
  hidden?: boolean;
  promotionsDisabled?: boolean;
}

export interface PlanFeature {
  feature: string;
  icon: LucideIcon;
}

export const PlanFeatures: PlanFeature[] = [
  {
    feature: "Fast Advisor matching within 24 hours",
    icon: Zap,
  },
  {
    feature: "Month-to-month plan, cancel any time",
    icon: IterationCcw,
  },
  {
    feature: "Happiness guarantee",
    icon: Smile,
  },
];

// const taskZapPlan: MembershipOption = {
//   name: "Pay-as-you-go",
//   supportHours: 1,
//   maxUsers: 1,
//   amountCents: 5000,
//   descriptionOverride: "1 hour of support a month.\nAdd hours at $50/hr",
//   promotionsDisabled: true,
// };
const peaceOfMind: MembershipOption = {
  name: "Peace of Mind",
  supportHours: 4,
  maxUsers: 1,
  amountCents: 16900,
  description:
    "Perfect for professionals who want occasional support during hectic weeks.",
};
const freeTimeFinder: MembershipOption = {
  name: "Free Time Finder",
  supportHours: 8,
  maxUsers: 2,
  amountCents: 29900,
  description: "Steady support to clear your backlog and start thinking ahead.",
};
const totalGameChanger: MembershipOption = {
  name: "Total Game Changer",
  supportHours: 16,
  maxUsers: 2,
  additionalFeatures: ["Priority response time"],
  amountCents: 56900,
  description: "For households juggling work, kids, and nonstop logistics.",
};
// const businessPro: MembershipOption = {
//   name: "Business Pro",
//   supportHours: 20,
//   maxUsers: 2,
//   additionalFeatures: ["Priority response time"],
//   amountCents: 119900,
// };
export const MEMBERSHIP_OPTIONS: MembershipOption[] = [
  // taskZapPlan,
  peaceOfMind,
  freeTimeFinder,
  totalGameChanger,
  // businessPro,
];

export const getDefaultPlanFromButton = (
  buttonParam?: string,
): number | null => {
  if (!buttonParam) return null;

  const buttonToMembership: { [key: string]: string } = {
    "get-started--peace-of-mind": "Peace of Mind",
    "get-started--free-time-finder": "Free Time Finder",
    "get-started--total-game-changer": "Total Game Changer",
  };

  const membershipName = buttonToMembership[buttonParam];
  if (!membershipName) return null;

  const index = MEMBERSHIP_OPTIONS.findIndex((m) => m.name === membershipName);
  return index >= 0 ? index : null;
};

export enum STEPS {
  GOALS = "goals",
  HOUSEHOLD = "household",
  SUPERPOWERS = "advisor-superpowers",
  JOIN_NOW = "join-now", // Forks
  TASKS_INTERRUPT = "tasks-interrupt",
  ABOUT_ADVISORS_INTERRUPT = "advisors-interrupt",
  CREATE_ACCOUNT = "create-account",

  // Get Started (onboarding) flow
  GET_STARTED_INTRO = "welcome",
  GET_STARTED_PROFILE = "profile",
  GET_STARTED_CREATE_ACCOUNT = "account-details",
  GET_STARTED_CONGRATS = "congrats",
  GET_STARTED_CALENDAR = "calendar",
  GET_STARTED_ADDRESS = "address",
  GET_STARTED_PARTNER = "partner",

  // Branch 1
  MEMBERSHIP = "membership",
  PAYMENT = "payment",
  SIGNUP_CONFIRMATION = "signup-confirmation",
  // Follow-up questions
  FUN = "fun",
  LIFE_EVENTS = "life-events",
  FAMILY_INFO = "family-info",
  TASKS = "tasks",
  // TODO_LIST = "todo-list",
  ANYTHING_ELSE = "anything-else",
  REFERRAL_SOURCE = "referral-source",
  FOLLOWUP_CONFIRMATION = "followup-confirmation",

  // Branch 2
  CONSULTATION_DETAILS = "consultation-details",
  CONSULTATION_TASKS = "consultation-tasks",
  CONSULTATION_SCHEDULE = "consultation-schedule",
  CONSULTATION_CONFIRMATION = "consultation-confirmation",

  // Upgrade flow
  UPGRADE_PAYMENT = "upgrade-payment",
}
